/**
 * 1. 配置了页面请求自动带上sentry-trace后，实际只在本地生效了，打包发布后无法生效。
 * 初步debug发现没有调用Sentry改写后的xhr.send方法，原因不明。
 * 2. Sentry只会在pageload及navigation时创建transaction，且会自动销毁。
 * 即使成功让Sentry自己带上sentry-trace header，也会有大量的请求没有附带sentry-trace头的情况。
 * 
 * 基于以上两点，决定给每个axios请求手动创建transaction及span，并加上sentry-trace header。
 */
import axios, { AxiosStatic, AxiosInstance } from 'axios';
import * as Sentry from '@sentry/react';
import moment from 'moment';

const TRANSACTION_OP = 'Custom Axios';
const TRANSACTION_MAX_AGE = 20;

/**
 * Additional options
 * @property {number} [maxAge] - transaction 存活的最长时间，单位秒，默认20s
 */
export interface SentryInterceptorOptions {
  maxAge?: number;
}

/**
 * Intercept the request of AxiosStatic or AxiosInstance
 * @params {AxiosStatic | AxiosInstance} [target] - aixos或者axios instance
 * @params {SentryInterceptorOptions} [options] - 可选附加选项
 */
export function interceptAxios(
  target: AxiosStatic,
  options?: SentryInterceptorOptions,
): AxiosStatic;
export function interceptAxios(
  target: AxiosInstance,
  options?: SentryInterceptorOptions,
): AxiosInstance;

export function interceptAxios(
  target: AxiosInstance | AxiosStatic,
  options?: SentryInterceptorOptions,
) {
  const timeout = options?.maxAge || TRANSACTION_MAX_AGE;

  target.interceptors.request.use(request => {
    try {
      if (
        process.env.NODE_ENV === 'production' &&
        window.__config__.enableSentry
      ) {
        let transaction = Sentry.getCurrentHub().getScope()?.getTransaction();

        if (!transaction || transaction.endTimestamp) {
          transaction = Sentry.getCurrentHub().startTransaction({
            name: ` Transaction Strat From: ${request.method?.toUpperCase()} ${
              request.url
            }`,
            op: TRANSACTION_OP,
            sampled: true,
          });
          Sentry.getCurrentHub().configureScope(scope =>
            scope.setSpan(transaction),
          );
        }

        const span = transaction.startChild({
          data: Object.assign(Object.assign({}, request.data), {
            type: 'xhr',
            method: request.method,
            url: request.url,
          }),
          description: request.method + ' ' + request.url,
          op: 'http.client',
        });

        const trace = span.toTraceparent();
        if (trace) {
          request.headers = {
            'sentry-trace': trace,
            ...request.headers,
          };
        }

        (request as any).tracing = { transaction: span };
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      return request;
    }
  });

  if (process.env.NODE_ENV === 'production' && window.__config__.enableSentry) {
    target.interceptors.response.use(
      response => {
        const span = (response.config as any).tracing?.transaction;
        const { transaction } = span || {};
        span?.finish();
        if (
          transaction?.op === TRANSACTION_OP &&
          transaction?.startTimestamp &&
          transaction.startTimestamp + timeout < moment().unix()
        ) {
          transaction?.finish();
        }
        return Promise.resolve(response);
      },
      error => {
        if (!error.config) {
          return Promise.reject(error);
        }
        const span = (error.config as any).tracing?.transaction;
        const { transaction } = span || {};
        span?.finish();
        if (transaction?.op === TRANSACTION_OP) {
          transaction?.finish();
        }
        return Promise.reject(error);
      },
    );
  }

  return target;
}

interceptAxios(axios);
const instance = interceptAxios(axios.create());

export { axios, instance };
